export type ThreadMessage = {
  id: number;
  author: "user" | "agent";
  text: string;
  createdAt: string;
  agentName?: string;
  translated?: boolean;
};

export type ThreadDetails = {
  id: number;
  user: {
    id: number;
    email: string;
    language: string;
    registeredAt: string;
    vipLevel: "silver" | "gold" | "vip";
  };
  messages: ThreadMessage[];
};

export type ThreadListItem = {
  id: number;
  userEmail: string;
  sourse: "chat" | "email";
  lang: string;
  type: "deposit" | "withdrawal" | "bonus" | "other";
  title: string;
  createdAt: string;
  updatedAt: string;
  assignee: string;
  status: "open" | "pending" | "resolved";
};

export type ThreadResponse = {
  meta: ThreadListItem;
  details: ThreadDetails;
};

export const mockThreadsList: ThreadListItem[] = [
  {
    id: 1543,
    userEmail: "john.player@email.com",
    sourse: "email",
    lang: "EN-GB",
    type: "deposit",
    title: "Payment method not working",
    createdAt: "2024-01-14 18:10",
    updatedAt: "2 min ago",
    assignee: "Sarah K.",
    status: "open",
  },
  {
    id: 2974,
    userEmail: "maria.rossi@email.com",
    sourse: "chat",
    lang: "IT-IT",
    type: "withdrawal",
    title: "Account verification pending",
    createdAt: "2024-01-14 17:30",
    updatedAt: "12 min ago",
    assignee: "Luca B.",
    status: "pending",
  },
  {
    id: 3026,
    userEmail: "ahmed.hassan@email.com",
    sourse: "email",
    lang: "EN-GB",
    type: "bonus",
    title: "Deposit bonus not applied",
    createdAt: "2024-01-14 16:00",
    updatedAt: "34 min ago",
    assignee: "Emily R.",
    status: "open",
  },
  {
    id: 4764,
    userEmail: "giulia.ferretti@email.com",
    sourse: "chat",
    lang: "IT-IT",
    type: "other",
    title: "Account blocked without reason",
    createdAt: "2024-01-14 14:30",
    updatedAt: "1 hour ago",
    assignee: "Marco D.",
    status: "resolved",
  },
  {
    id: 1843,
    userEmail: "lisa.chen@email.com",
    sourse: "email",
    lang: "EN-GB",
    type: "deposit",
    title: "Deposit not received",
    createdAt: "2024-01-14 13:50",
    updatedAt: "2 hours ago",
    assignee: "Daniel P.",
    status: "open",
  },
  {
    id: 1842,
    userEmail: "max.mueller@email.com",
    sourse: "email",
    lang: "DE-DE",
    type: "withdrawal",
    title: "Withdrawal delayed",
    createdAt: "2024-01-14 12:40",
    updatedAt: "3 hours ago",
    assignee: "Anna S.",
    status: "pending",
  },
];

export const mockThreadDetails: ThreadDetails[] = [
  {
    id: 1543,
    user: {
      id: 1432,
      email: "john.player@email.com",
      language: "EN-GB",
      registeredAt: "2023-04-12",
      vipLevel: "silver",
    },
    messages: [
      {
        id: 1,
        author: "user",
        text: "Hi, I tried to make a deposit using my credit card, but it was declined. I’m not sure what went wrong.",
        createdAt: "2024-01-14 18:10",
      },
      {
        id: 2,
        author: "agent",
        text: "Hello John, thanks for reaching out. I’m sorry to hear about the issue with your deposit. Let me check this for you.",
        createdAt: "2024-01-14 18:12",
        agentName: "Sarah K.",
      },
      {
        id: 3,
        author: "user",
        text: "I’ve tried three times already. The card is valid and works on other websites.",
        createdAt: "2024-01-14 18:15",
      },
      {
        id: 4,
        author: "agent",
        text: "Understood. Sometimes transactions can be blocked by the payment provider or the issuing bank. I’m checking the logs now.",
        createdAt: "2024-01-14 18:18",
        agentName: "Sarah K.",
      },
      {
        id: 5,
        author: "agent",
        text: "It looks like the transaction was declined by your bank due to security restrictions. I recommend contacting them directly or trying another payment method.",
        createdAt: "2024-01-14 18:22",
        agentName: "Sarah K.",
        translated: true,
      },
      {
        id: 6,
        author: "user",
        text: "Alright, I’ll contact my bank and try again later. Thanks for your help.",
        createdAt: "2024-01-14 18:25",
      },
    ],
  },
  {
    id: 2974,
    user: {
      id: 2975,
      email: "maria.rossi@email.com",
      language: "IT-IT",
      registeredAt: "2022-11-03",
      vipLevel: "gold",
    },
    messages: [
      {
        id: 1,
        author: "user",
        text: "Hello, my account verification is still pending and it has been several days.",
        createdAt: "2024-01-14 17:20",
      },
      {
        id: 2,
        author: "agent",
        text: "Hi Maria, thank you for your message. I can see your verification request and I’m checking its status now.",
        createdAt: "2024-01-14 17:25",
        agentName: "Luca B.",
      },
      {
        id: 3,
        author: "user",
        text: "Is there anything else I need to upload? I already sent my ID and proof of address.",
        createdAt: "2024-01-14 17:30",
      },
      {
        id: 4,
        author: "agent",
        text: "Everything looks correct on your side. The verification is currently under review by our compliance team.",
        createdAt: "2024-01-14 17:35",
        agentName: "Luca B.",
      },
      {
        id: 5,
        author: "agent",
        text: "As a Gold VIP user, your verification should be completed shortly. We appreciate your patience.",
        createdAt: "2024-01-14 17:40",
        agentName: "Luca B.",
        translated: true,
      },
    ],
  },
  {
    id: 3026,
    user: {
      id: 8642,
      email: "ahmed.hassan@email.com",
      language: "EN-GB",
      registeredAt: "2021-08-19",
      vipLevel: "vip",
    },
    messages: [
      {
        id: 1,
        author: "user",
        text: "Hello, I was expecting a deposit bonus but it was not added to my balance.",
        createdAt: "2024-01-14 16:00",
      },
      {
        id: 2,
        author: "agent",
        text: "Hi Ahmed, thanks for contacting us. Let me review your bonus eligibility.",
        createdAt: "2024-01-14 16:05",
        agentName: "Emily R.",
      },
      {
        id: 3,
        author: "agent",
        text: "The bonus was not applied because the minimum deposit amount was not met. I’ve manually added it as a one-time exception.",
        createdAt: "2024-01-14 16:10",
        agentName: "Emily R.",
        translated: true,
      },
      {
        id: 4,
        author: "user",
        text: "Thank you very much, I appreciate the support.",
        createdAt: "2024-01-14 16:12",
      },
    ],
  },
  {
    id: 4764,
    user: {
      id: 4234,
      email: "giulia.ferretti@email.com",
      language: "IT-IT",
      registeredAt: "2020-06-30",
      vipLevel: "silver",
    },
    messages: [
      {
        id: 1,
        author: "user",
        text: "My account was suddenly blocked and I don’t know why.",
        createdAt: "2024-01-14 14:50",
      },
      {
        id: 2,
        author: "agent",
        text: "Hello Giulia, your account was temporarily blocked due to unusual activity. I’m reviewing it now.",
        createdAt: "2024-01-14 15:00",
        agentName: "Marco D.",
      },
      {
        id: 3,
        author: "agent",
        text: "The issue has been resolved and your account is now fully active again.",
        createdAt: "2024-01-14 15:45",
        agentName: "Marco D.",
        translated: true,
      },
      {
        id: 4,
        author: "user",
        text: "Great, thank you for the quick response!",
        createdAt: "2024-01-14 15:47",
      },
    ],
  },
  {
    id: 1843,
    user: {
      id: 1843,
      email: "lisa.chen@email.com",
      language: "EN-GB",
      registeredAt: "2023-02-15",
      vipLevel: "silver",
    },
    messages: [
      {
        id: 1,
        author: "user",
        text: "Hello, my deposit hasn't arrived yet.",
        createdAt: "2024-01-14 13:55",
      },
      {
        id: 2,
        author: "agent",
        text: "Hi Lisa, let me check your account. I’ll update you shortly.",
        createdAt: "2024-01-14 14:00",
        agentName: "Daniel P.",
      },
      {
        id: 3,
        author: "agent",
        text: "It appears there was a processing delay. The funds should appear in your account soon.",
        createdAt: "2024-01-14 14:10",
        agentName: "Daniel P.",
        translated: true,
      },
      {
        id: 4,
        author: "user",
        text: "Thank you, I will monitor my account.",
        createdAt: "2024-01-14 14:15",
      },
    ],
  },
  {
    id: 1842,
    user: {
      id: 1842,
      email: "max.mueller@email.com",
      language: "DE-DE",
      registeredAt: "2022-03-20",
      vipLevel: "gold",
    },
    messages: [
      {
        id: 1,
        author: "user",
        text: "My withdrawal is delayed, can you help?",
        createdAt: "2024-01-14 12:45",
      },
      {
        id: 2,
        author: "agent",
        text: "Hi Max, I’m reviewing your withdrawal request.",
        createdAt: "2024-01-14 12:50",
        agentName: "Anna S.",
      },
      {
        id: 3,
        author: "agent",
        text: "The bank is processing the payment, it should arrive soon.",
        createdAt: "2024-01-14 13:00",
        agentName: "Anna S.",
        translated: true,
      },
      {
        id: 4,
        author: "user",
        text: "Great, thanks for the update!",
        createdAt: "2024-01-14 13:05",
      },
    ],
  },
];
